import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import _ from 'lodash';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import UpdateOrderLineItems from '../../../services/UpdateOrderLineItems';
import PRTNRDeviceSectionView from '../../../views/PRTNRDeviceSectionView';

import tmplPRTNRFormView from './PRTNRA3FormView.hbs';

const PRTNRA3FormView = View.extend({
  template: tmplPRTNRFormView,

  ui: {
    a3DescriptionSection: '.a3-description-section',
    a3NIPRSection: '.a3-nipr-section',
    a3SIPRSection: '.a3-sipr-section',
    a3ServicesSection: '.a3-services-section',
    devicesAlert: '.no-devices-error'
  },

  regions: {
    a3DescriptionSection: '@ui.a3DescriptionSection',
    a3NIPRSection: '@ui.a3NIPRSection',
    a3SIPRSection: '@ui.a3SIPRSection',
    a3ServicesSection: '@ui.a3ServicesSection',
    prodServicesSection: '@ui.prodServicesSection'
  },

  className: 'prtnr-form-view',

  onRender() {
    const orderType = this.model.get('orderDetails').orderTypeId;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    const { A3NIPR, A3SIPR, A3Services } = _.groupBy(this.model.get('lineItems'), 'panel');

    this.showChildView(
      'a3DescriptionSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          contract: 'A3',
          id: 'A3-Description',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'a3NIPRSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: A3NIPR,
          title: 'NIPR Devices',
          orderType,
          type: 'devices',
          contract: 'A3',
          id: 'A3-NIPR',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'a3SIPRSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: A3SIPR,
          title: 'SIPR Devices',
          orderType,
          type: 'devices',
          contract: 'A3',
          id: 'A3-SIPR',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'a3ServicesSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: A3Services,
          title: 'Additional Services',
          orderType,
          type: 'services',
          contract: 'A3',
          id: 'A3-Services',
          isAdminEdit
        })
      })
    );
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving devices for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  showNoChangedDevicesError() {
    showErrorMessage({
      errorCode: 'noChangedDevicesError',
      errorTitle: 'No Devices Updated Error',
      errorMessage:
        'No devices were updated. Please update devices in order to save changes.'
    });
  },

  async validate() {
    const [niprColl, siprColl, servicesColl] = await this.getDeviceData();

    const totalMonoQuantity = niprColl.totalMonoQuantity + siprColl.totalMonoQuantity;
    const totalColorQuantity = niprColl.totalColorQuantity + siprColl.totalColorQuantity;

    let isValid = true;
    if (
      !totalMonoQuantity &&
      !totalColorQuantity &&
      !servicesColl.totalMonthlyAdditionalServicesQuantity &&
      !servicesColl.totalYearlyOneTimeServicesQuantity
    ) {
      isValid = false;
      this.ui.devicesAlert.show();
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  async getDeviceData() {
    return Promise.all([
      this.getChildView('a3NIPRSection').getDeviceData(),
      this.getChildView('a3SIPRSection').getDeviceData(),
      this.getChildView('a3ServicesSection').getDeviceData()
    ]);
  },

  getLineItemData(lineItemData) {
    const lineItems = this.model.get('lineItems');
    const changedItems = [];

    _.each(lineItems, (item) => {
      const changedPrice = lineItemData[`${item.id}-price`];
      const changedConfig = lineItemData[`${item.id}-configurationAndPricing`];
      const changedDesc = lineItemData[`${item.id}-description`];
      const changedDlaCost = lineItemData[`${item.id}-dlaCost`];
      
      const isPriceChanged = (item.price !== changedPrice) && !!item.price;
      const isConfigChanged = (item.configurationAndPricing !== changedConfig) && !!item.configurationAndPricing;
      const isDescChanged = (item.description !== changedDesc) && !!item.description
      const isDlaCostChanged = (item.dlaCost !== changedDlaCost) && !!item.dlaCost;
      
      const changedItem = {};
      if (isPriceChanged) changedItem.price = (parseFloat(changedPrice)).toFixed(2);
      if (isConfigChanged) changedItem.configurationAndPricing = changedConfig;
      if (isDescChanged) changedItem.description = changedDesc;
      if (isDlaCostChanged) changedItem.dlaCost = changedDlaCost;
      if (!_.isEmpty(changedItem)) {
        changedItem.id = item.id;
        changedItems.push(changedItem);
      }
    })

    return changedItems;
  },

  async onSave(lineItemData) {
    this.ui.devicesAlert.hide();
    this.trigger('no:errors');
    let formData;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    try {
      if (isAdminEdit && lineItemData) {
        formData = await this.saveAdminEdit(lineItemData);
        if (formData.changedItems.length > 0) {
          await UpdateOrderLineItems(formData);
          this.showSaveSuccess();
        } else {
          this.showNoChangedDevicesError();
        }
      } else {
        formData = await this.saveOrder();
        await Promise.all([SaveCustomerOrderDevices(formData), ReloadViewModule.reload()]);
        this.showSaveSuccess();
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  async saveOrder() {
    const [niprColl, siprColl, servicesColl] = await this.getDeviceData();

    const devicesFormData = niprColl.changedItems.concat(siprColl.changedItems, servicesColl.changedItems);

    const formData = {
      devicesFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true,
      finPocExists: true
    };

    return formData;
  },

  async saveAdminEdit(lineItemData) {
    const changedItems = this.getLineItemData(lineItemData);
    const formData = {
      orderFormTypeID: this.model.get('orderDetails').orderTypeId,
      changedItems
    };

    return formData;
  }
});

export default PRTNRA3FormView;
